import React, { useEffect, useState, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import "leaflet/dist/leaflet.css";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  CircleMarker,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import { Calendar, Search, X, MapPin, Truck, User, Navigation, Clock, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import RecenterButton from "../components/RecenterButton";
import api from "../services/api";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import EmployeeLayout from "../components/EmployeeLayout";
import ProfilePictureCropper from "../components/ProfilePictureCropper";
import UserDetailsOverlay from "../components/UserDetailsOverlay";
import {
  uploadProfilePicture,
  getProfilePictureUrl,
} from "../services/userService";
import { defaultUserImg } from "../assets/defaultUser";
import { getAllBookings } from "../services/bookingService";

// Fix Leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png").default,
  iconUrl: require("leaflet/dist/images/marker-icon.png").default,
  shadowUrl: require("leaflet/dist/images/marker-shadow.png").default,
});

// Vehicle icons
import AllIcon from "../assets/all.png";
import CarIcon from "../assets/car.png";
import VanIcon from "../assets/van.png";
import BusIcon from "../assets/bus.png";
import SuvIcon from "../assets/suv.png";
import CabIcon from "../assets/cab.png";
import ForkliftIcon from "../assets/fork lift.png";
import LorryIcon from "../assets/lorry.png";
import CherryIcon from "../assets/cherry.png";
import CraneIcon from "../assets/crane.jpg";

const TYPE_ICONS = {
  C: CarIcon,
  V: VanIcon,
  F: ForkliftIcon,
  L: LorryIcon,
  S: SuvIcon,
  CA: CabIcon,
  B: BusIcon,
  CH: CherryIcon,
  T: LorryIcon,
  CR: CraneIcon,
};

const SRI_LANKA_CENTER = [6.957647, 79.855336];
const SRI_LANKA_BOUNDS = [
  [6.959564, 79.852654],
  [6.959779, 79.859053],
];

const createIcon = (code, selected = false) => {
  const url = TYPE_ICONS[code] || TYPE_ICONS.C;
  return new L.Icon({
    iconUrl: url,
    iconSize: selected ? [40, 40] : [32, 32],
    iconAnchor: [selected ? 20 : 16, selected ? 40 : 32],
    popupAnchor: [0, selected ? -40 : -32],
  });
};

const getVehicleCode = (type) => {
  if (!type) return "C";
  const t = type.toString().trim().toLowerCase();
  if (t.includes("car")) return "C";
  if (t.includes("van")) return "V";
  if (t.includes("bus")) return "B";
  if (t.includes("suv")) return "S";
  if (t.includes("cab")) return "CA";
  if (t.includes("forklift")) return "F";
  if (t.includes("cherry")) return "CH";
  if (t.includes("trailer") || t === "t") return "T";
  if (t.includes("crane") || t === "cr") return "CR";
  if (t.includes("lorry")) return "L";
  return "C";
};

const VEHICLE_FILTERS = [
  { code: "ALL", label: "All", icon: AllIcon },
  { code: "C", label: "Car", icon: TYPE_ICONS.C },
  { code: "V", label: "Van", icon: TYPE_ICONS.V },
  { code: "B", label: "Bus", icon: TYPE_ICONS.B },
  { code: "S", label: "SUV", icon: TYPE_ICONS.S },
  { code: "CA", label: "Cab", icon: TYPE_ICONS.CA },
  { code: "F", label: "Forklift", icon: TYPE_ICONS.F },
  { code: "L", label: "Lorry", icon: TYPE_ICONS.L },
  { code: "CH", label: "Cherry-Picker", icon: TYPE_ICONS.CH },
  { code: "T", label: "Trailer", icon: TYPE_ICONS.T },
  { code: "CR", label: "Crane", icon: TYPE_ICONS.CR },
];

// Stat Card Component
function StatCard({ title, value, icon: Icon, color, onClick, filter }) {
  const colorClasses = {
    blue: "bg-blue-50 border-blue-200 text-blue-600",
    green: "bg-emerald-50 border-emerald-200 text-emerald-600",
    yellow: "bg-yellow-50 border-yellow-200 text-yellow-600",
    purple: "bg-purple-50 border-purple-200 text-purple-600",
    red: "bg-red-50 border-red-200 text-red-600",
  };
  
  const bgColor = colorClasses[color] || colorClasses.blue;
  
  return (
    <div
      onClick={onClick}
      className={`${bgColor} border rounded-lg p-3 cursor-pointer transition-all hover:shadow-md hover:scale-105`}
    >
      <div className="flex items-center justify-between mb-1">
        <Icon size={16} className={bgColor.split(' ')[2]} />
        <span className="text-lg font-bold">{value}</span>
      </div>
      <p className="text-xs font-medium">{title}</p>
    </div>
  );
}

// Filter Button Component
function FilterButton({ filterItem, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all flex-shrink-0 ${
        isActive
          ? "bg-gray-900 text-white shadow-sm"
          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
      }`}
    >
      <img src={filterItem.icon} alt={filterItem.label} className="w-4 h-4" />
      <span className="hidden sm:inline">{filterItem.label}</span>
      <span className="sm:hidden">{filterItem.label.charAt(0)}</span>
    </button>
  );
}

// Vehicle Card Component
function VehicleCard({ vehicle, driverName, isSelected, onSelect, onBook, isBooking, isValidating }) {
  const bStatus = (vehicle.bStatus || "").toString().trim().toUpperCase();
  const hasPendingStatus = bStatus === "P" || bStatus === "PENDING";
  const isAvailable = !hasPendingStatus;
  
  return (
    <div
      onClick={() => onSelect(vehicle)}
      className={`p-2 rounded-lg cursor-pointer transition-all ${
        isSelected
          ? "bg-blue-50 border-2 border-blue-500"
          : "bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm"
      }`}
    >
      <div className="flex items-center gap-2">
        <img
          src={TYPE_ICONS[getVehicleCode(vehicle.vehicle_type || vehicle.vehicleType)] || CarIcon}
          alt={vehicle.vehicleType}
          className="w-8 h-8 object-contain"
        />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-gray-900 truncate">
            {vehicle.vehicleId}
          </p>
          <p className="text-xs text-gray-500 truncate">{driverName}</p>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onBook(vehicle);
          }}
          disabled={!isAvailable || isBooking || isValidating}
          className={`px-2 py-1 rounded text-xs font-medium transition ${
            !isAvailable || isBooking || isValidating
              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
              : "bg-emerald-600 text-white hover:bg-emerald-700"
          }`}
        >
          {isValidating ? "..." : isBooking ? "..." : isAvailable ? "Book" : "N/A"}
        </button>
      </div>
    </div>
  );
}

function PanToVehicle({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, 15, { duration: 1.0 });
  }, [position, map]);
  return null;
}

export default function EmployeeDashboard() {
  const { userDetails, authKey } = useSelector((state) => state.auth);
  const [vehicles, setVehicles] = useState([]);
  const [driverNames, setDriverNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [currentPosition, setCurrentPosition] = useState(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [selectedVehiclePosition, setSelectedVehiclePosition] = useState(null);
  const [autocompleteList, setAutocompleteList] = useState([]);
  const [locationError, setLocationError] = useState("");
  const navigate = useNavigate();
  const searchRef = useRef();
  const [bookingInProgress, setBookingInProgress] = useState(null);
  const [pendingReservation, setPendingReservation] = useState(null);
  const [isCancellingPending, setIsCancellingPending] = useState(false);
  const [isUploadingPic, setIsUploadingPic] = useState(false);
  const [isUpdatingMobile, setIsUpdatingMobile] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef(null);
  const [isProfileDetailsVisible, setIsProfileDetailsVisible] = useState(false);
  const [vehicleValidationError, setVehicleValidationError] = useState("");
  const [isValidatingVehicle, setIsValidatingVehicle] = useState(false);

  // Employee details state
  const [employee, setEmployee] = useState({
    name: "",
    employeeId: "",
    mobileNo: "",
    email: "",
    profilePic: defaultUserImg,
    role: "E",
    status: "A",
    password: "",
    file_path: null,
  });

  // Booking statistics state
  const [bookingStats, setBookingStats] = useState({
    total: 0,
    active: 0,
    pending: 0,
    completed: 0,
    cancelled: 0,
  });

  // Fetch employee details
  useEffect(() => {
    const fetchEmployeeDetails = async () => {
      if (!authKey || !userDetails?.ServiceNo) return;
      try {
        const res = await api.get("/UserDetails/GetUserDetails");
        if (res.data?.resultSet) {
          const thisEmployee = res.data.resultSet.find(
            (u) => u.ServiceNo === userDetails.ServiceNo,
          );
          if (thisEmployee) {
            setEmployee({
              name: thisEmployee.Name || "Employee",
              employeeId: thisEmployee.ServiceNo || "",
              mobileNo: thisEmployee.MobileNo || "N/A",
              email: thisEmployee.Email || "N/A",
              profilePic: getProfilePictureUrl(thisEmployee.ServiceNo),
              role: thisEmployee.role || "E",
              status: thisEmployee.status || "A",
              password: thisEmployee.password || "",
              file_path: thisEmployee.file_path || null,
            });
          }
        }
      } catch (err) {
        console.error("Failed to fetch employee details:", err);
      }
    };
    fetchEmployeeDetails();
    fetchBookingStats();
  }, [authKey, userDetails?.ServiceNo]);

  // Fetch booking statistics
  const fetchBookingStats = async () => {
    try {
      const allBookings = await getAllBookings();
      const myBookings = allBookings.filter(
        (b) => b.bookingSno === userDetails?.ServiceNo,
      );
      
      const stats = {
        total: myBookings.length,
        active: myBookings.filter((b) => {
          const status = (b.status || "").toString().trim().toUpperCase();
          return status === "A" || status === "ACTIVE";
        }).length,
        pending: myBookings.filter((b) => {
          const status = (b.status || "").toString().trim().toUpperCase();
          return status === "P" || status === "PENDING";
        }).length,
        completed: myBookings.filter((b) => {
          const status = (b.status || "").toString().trim().toUpperCase();
          return status === "CO" || status === "COMPLETED";
        }).length,
        cancelled: myBookings.filter((b) => {
          const status = (b.status || "").toString().trim().toUpperCase();
          return status === "C" || status === "CANCELLED" || status === "CANCELED";
        }).length,
      };
      
      setBookingStats(stats);
    } catch (error) {
      console.error("Failed to fetch booking stats:", error);
    }
  };

  // Fetch vehicles
  useEffect(() => {
    const fetchVehicles = async () => {
      if (!authKey) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const res = await api.get("/VehicleDetails/GetVehicleDetails");
        if (res.data?.resultSet && Array.isArray(res.data.resultSet)) {
          const visibleVehicles = res.data.resultSet.filter((v) => {
            const bStatus = (v.bStatus ?? "").toString().trim().toUpperCase();
            const oStatus = (v.oStatus ?? "").toString().trim().toUpperCase();
            const isOffline = oStatus === "F" || oStatus === "OFFLINE";
            const isBooking = bStatus === "B" || bStatus === "BOOKING";
            return !isOffline && !isBooking;
          });
          setVehicles(visibleVehicles);
        } else {
          setVehicles([]);
        }
      } catch (err) {
        console.error("Failed to fetch vehicles:", err);
        setVehicles([]);
      } finally {
        setLoading(false);
      }
    };
    fetchVehicles();
  }, [authKey]);

  // Get user location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setCurrentPosition([pos.coords.latitude, pos.coords.longitude]);
          setLocationError("");
        },
        () => setLocationError("Location access denied"),
        { enableHighAccuracy: true },
      );
    } else {
      setLocationError("Geolocation not supported");
    }
  }, []);

  const filteredVehicles = useMemo(() => {
    return vehicles.filter((v) => {
      const code = getVehicleCode(v.vehicle_type || v.vehicleType);
      const matchesFilter = filter === "ALL" || code === filter;
      const driverName = driverNames[v.driverSno] || "";
      const matchesSearch =
        !search ||
        v.vehicleId?.toString().toLowerCase().includes(search.toLowerCase()) ||
        v.driverSno?.toString().toLowerCase().includes(search.toLowerCase()) ||
        driverName.toLowerCase().includes(search.toLowerCase());
      return matchesFilter && matchesSearch;
    });
  }, [vehicles, filter, search, driverNames]);

  const handleSelectVehicle = (v) => {
    setSelectedVehicleId(v.vehicleId);
    const lat = parseFloat(v.latitude);
    const lng = parseFloat(v.longitude);
    const position = [
      !isNaN(lat) && lat !== 0 ? lat : currentPosition ? currentPosition[0] : SRI_LANKA_CENTER[0],
      !isNaN(lng) && lng !== 0 ? lng : currentPosition ? currentPosition[1] : SRI_LANKA_CENTER[1],
    ];
    setSelectedVehiclePosition(position);
  };

  const validateVehicleForBooking = async (vehicleId) => {
    setIsValidatingVehicle(true);
    setVehicleValidationError("");
    
    try {
      const vehicleRes = await api.get("/VehicleDetails/GetVehicleDetails");
      if (vehicleRes.data?.statusCode === 200) {
        const selectedVehicle = vehicleRes.data.resultSet.find(
          (v) => v.vehicleId === vehicleId,
        );
        
        if (!selectedVehicle) {
          setVehicleValidationError("Vehicle not found");
          setIsValidatingVehicle(false);
          return { isValid: false };
        }
        
        const oStatus = (selectedVehicle.oStatus || "").toString().trim().toUpperCase();
        if (oStatus === "F" || oStatus === "OFFLINE") {
          setVehicleValidationError("Driver is offline");
          setIsValidatingVehicle(false);
          return { isValid: false };
        }
        
        const bStatus = (selectedVehicle.bStatus || "").toString().trim().toUpperCase();
        if (bStatus === "B" || bStatus === "BOOKING") {
          setVehicleValidationError("Driver is already booked");
          setIsValidatingVehicle(false);
          return { isValid: false };
        }
        
        setIsValidatingVehicle(false);
        return { isValid: true };
      } else {
        setVehicleValidationError("Failed to verify");
        setIsValidatingVehicle(false);
        return { isValid: false };
      }
    } catch (err) {
      console.error("Validation error:", err);
      setVehicleValidationError("Error checking availability");
      setIsValidatingVehicle(false);
      return { isValid: false };
    }
  };

  const handleBook = async (vehicle) => {
    if (bookingInProgress || !vehicle?.vehicleId) return;
    
    setBookingInProgress(vehicle.vehicleId);
    const validationResult = await validateVehicleForBooking(vehicle.vehicleId);
    
    if (!validationResult.isValid) {
      setTimeout(() => {
        setBookingInProgress(null);
        window.location.reload();
      }, 3000);
      return;
    }
    
    try {
      const vehiclePayload = [{
        vehicleId: vehicle.vehicleId,
        vehicleType: getVehicleCode(vehicle.vehicle_type || vehicle.vehicleType),
        weightCat: vehicle.weightCat || "",
        driverSno: vehicle.driverSno || "",
        status: "A",
        bStatus: "P",
        oStatus: "O",
        latitude: vehicle.latitude,
        longitude: vehicle.longitude,
      }];
      
      await api.post("/VehicleRegister/PostVehicleDetails", vehiclePayload);
      
      const expiryTime = Date.now() + 100 * 1000;
      const pendingData = { vehicle, isPendingLock: true, expiryTime };
      localStorage.setItem("pending_employee_booking", JSON.stringify(pendingData));
      setPendingReservation(pendingData);
      
      navigate("/employee/book", {
        state: { vehicle, isPendingLock: true },
      });
    } catch (err) {
      console.error("Failed to reserve:", err);
      alert("Vehicle already reserved by someone else!");
    } finally {
      setBookingInProgress(null);
    }
  };

  const handleManualCancelPending = async () => {
    if (!pendingReservation || isCancellingPending) return;
    
    const confirmed = window.confirm("Cancel this vehicle selection?");
    if (!confirmed) return;
    
    setIsCancellingPending(true);
    try {
      const pendingVehicle = pendingReservation.vehicle || pendingReservation;
      const vehicleId = pendingVehicle?.vehicleId || pendingVehicle?.vId;
      
      if (vehicleId) {
        const vehicleTypeCode = getVehicleCode(pendingVehicle.vehicle_type || pendingVehicle.vehicleType);
        const payload = [{
          vehicleId: vehicleId,
          vehicleType: vehicleTypeCode,
          weightCat: pendingVehicle.weightCat || "",
          driverSno: pendingVehicle.driverSno || pendingVehicle.dSNo || "",
          status: "A",
          bStatus: "",
          oStatus: "O",
          latitude: pendingVehicle.latitude || "",
          longitude: pendingVehicle.longitude || "",
        }];
        
        await api.post("/VehicleRegister/PostVehicleDetails", payload);
        setVehicles((prev) =>
          prev.map((v) =>
            v.vehicleId?.toString() === vehicleId?.toString()
              ? { ...v, bStatus: "" }
              : v
          )
        );
      }
      
      localStorage.removeItem("pending_employee_booking");
      setPendingReservation(null);
      setBookingInProgress(null);
      alert("Vehicle selection cancelled");
    } catch (err) {
      console.error("Cancel failed:", err);
      alert("Failed to cancel");
    } finally {
      setIsCancellingPending(false);
    }
  };

  // Autocomplete suggestions
  useEffect(() => {
    if (!search.trim()) {
      setAutocompleteList([]);
      return;
    }
    const timer = setTimeout(() => {
      const searchLower = search.toLowerCase();
      const suggestions = [
        ...vehicles
          .filter((v) => v.driverSno?.toString().toLowerCase().includes(searchLower))
          .map((v) => ({ type: "driverSno", value: v.driverSno, label: `Driver ID: ${v.driverSno}` })),
        ...vehicles
          .filter((v) => {
            const driverName = driverNames[v.driverSno] || "";
            return driverName.toLowerCase().includes(searchLower);
          })
          .map((v) => ({ type: "driverName", value: driverNames[v.driverSno], label: `Driver: ${driverNames[v.driverSno]}` })),
        ...vehicles
          .filter((v) => v.vehicleId?.toString().toLowerCase().includes(searchLower))
          .map((v) => ({ type: "vehicleId", value: v.vehicleId, label: `Vehicle: ${v.vehicleId}` })),
      ];
      
      const uniqueSuggestions = [];
      const seen = new Set();
      for (const suggestion of suggestions) {
        const key = `${suggestion.type}-${suggestion.value}`;
        if (!seen.has(key)) {
          seen.add(key);
          uniqueSuggestions.push(suggestion);
          if (uniqueSuggestions.length >= 5) break;
        }
      }
      setAutocompleteList(uniqueSuggestions);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, vehicles, driverNames]);

  const handleSelectAutocomplete = (suggestion) => {
    setSearch(suggestion.value);
    setAutocompleteList([]);
  };

  return (
    <EmployeeLayout>
      {/* Header Section */}
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
              Welcome back, {employee.name.split(' ')[0]}
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Track vehicles and manage your bookings
            </p>
          </div>
          <button
            onClick={() => navigate("/employee/schedule-booking")}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition shadow-sm"
          >
            <Calendar size={14} />
            Schedule Booking
          </button>
        </div>
      </div>

      {/* Profile Section */}
      <div className="bg-white border border-gray-100 rounded-lg p-3 mb-4">
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <div className="relative flex-shrink-0">
            <img
              src={employee.profilePic}
              alt="Profile"
              onClick={() => setIsProfileDetailsVisible(true)}
              className="w-16 h-16 rounded-full object-cover border-2 border-gray-200 cursor-pointer hover:border-gray-400 transition"
              onError={(e) => { e.target.src = defaultUserImg; }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploadingPic}
              className="absolute bottom-0 right-0 bg-gray-900 hover:bg-gray-800 text-white rounded-full p-1 shadow-lg transition"
            >
              {isUploadingPic ? (
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                const reader = new FileReader();
                reader.onload = () => {
                  setSelectedImage(reader.result);
                  setShowCropper(true);
                };
                reader.readAsDataURL(file);
              }
            }} className="hidden" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h2 className="text-base font-semibold text-gray-900">{employee.name}</h2>
            <p className="text-xs text-gray-500">ID: {employee.employeeId}</p>
            <p className="text-xs text-gray-500">Mobile: {employee.mobileNo}</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        <StatCard title="Total" value={bookingStats.total} icon={ClipboardList} color="blue" onClick={() => navigate("/employee/my-bookings")} />
        <StatCard title="Active" value={bookingStats.active} icon={CheckCircle} color="green" onClick={() => navigate("/employee/my-bookings?filter=active")} />
        <StatCard title="Pending" value={bookingStats.pending} icon={Clock} color="yellow" onClick={() => navigate("/employee/my-bookings?filter=pending")} />
        <StatCard title="Completed" value={bookingStats.completed} icon={CheckCircle} color="purple" onClick={() => navigate("/employee/my-bookings?filter=completed")} />
        <StatCard title="Cancelled" value={bookingStats.cancelled} icon={XCircle} color="red" onClick={() => navigate("/employee/my-bookings?filter=cancelled")} />
      </div>

      {/* Available Vehicles Title */}
      <div className="mb-3">
        <h2 className="text-sm font-semibold text-gray-900">Available Vehicles</h2>
        <p className="text-xs text-gray-500">Select a vehicle to book</p>
      </div>

      {/* Search Bar */}
      <div className="mb-3 relative" ref={searchRef}>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by vehicle ID, driver ID, or driver name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2">
              <X size={12} className="text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
        {autocompleteList.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-32 overflow-auto">
            {autocompleteList.map((suggestion, index) => (
              <li
                key={index}
                onClick={() => handleSelectAutocomplete(suggestion)}
                className="px-3 py-1.5 text-xs hover:bg-gray-50 cursor-pointer"
              >
                {suggestion.label}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Filter Buttons */}
      <div className="flex flex-nowrap gap-1.5 overflow-x-auto pb-2 mb-3">
        {VEHICLE_FILTERS.map((f) => (
          <FilterButton
            key={f.code}
            filterItem={f}
            isActive={filter === f.code}
            onClick={() => setFilter(f.code)}
          />
        ))}
      </div>

      {locationError && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mb-3">
          <p className="text-xs text-yellow-700">{locationError}</p>
        </div>
      )}

      {/* Map and Vehicle List - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Map Section */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden">
            <div className="p-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <MapPin size={14} className="text-gray-600" />
                <h2 className="text-sm font-semibold text-gray-900">Live Map View</h2>
              </div>
            </div>
            <div className="h-[400px] sm:h-[500px] w-full">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
                </div>
              ) : (
                <MapContainer
                  center={currentPosition || SRI_LANKA_CENTER}
                  zoom={13}
                  className="h-full w-full"
                  style={{ zIndex: 1 }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='© OpenStreetMap'
                  />
                  <RecenterButton position={currentPosition} zoom={14} />
                  {selectedVehiclePosition && <PanToVehicle position={selectedVehiclePosition} />}
                  {currentPosition && (
                    <CircleMarker
                      center={currentPosition}
                      radius={6}
                      color="#3B82F6"
                      fillColor="#3B82F6"
                      fillOpacity={0.7}
                    >
                      <Popup>Your Location</Popup>
                    </CircleMarker>
                  )}
                  {filteredVehicles.map((v) => {
                    const code = getVehicleCode(v.vehicle_type || v.vehicleType);
                    const isSelected = v.vehicleId === selectedVehicleId;
                    const icon = createIcon(code, isSelected);
                    const lat = parseFloat(v.latitude);
                    const lng = parseFloat(v.longitude);
                    const position = [
                      !isNaN(lat) && lat !== 0 ? lat : currentPosition ? currentPosition[0] : SRI_LANKA_CENTER[0],
                      !isNaN(lng) && lng !== 0 ? lng : currentPosition ? currentPosition[1] : SRI_LANKA_CENTER[1],
                    ];
                    
                    return (
                      <Marker
                        key={v.vehicleId}
                        position={position}
                        icon={icon}
                        eventHandlers={{ click: () => handleSelectVehicle(v) }}
                      >
                        <Popup>
                          <div className="text-center min-w-[140px]">
                            <p className="font-semibold text-gray-900 text-sm">{v.vehicleType}</p>
                            <p className="text-xs text-gray-600">{v.vehicleId}</p>
                            <p className="text-xs text-gray-500 mt-1">Driver: {driverNames[v.driverSno] || v.driverSno}</p>
                            <button
                              onClick={() => handleBook(v)}
                              disabled={bookingInProgress === v.vehicleId || isValidatingVehicle}
                              className="mt-2 w-full px-2 py-1 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700 transition disabled:opacity-50"
                            >
                              {bookingInProgress === v.vehicleId ? "Booking..." : "Book Now"}
                            </button>
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
                </MapContainer>
              )}
            </div>
          </div>
        </div>

        {/* Vehicle List Section */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-gray-100 rounded-lg shadow-sm">
            <div className="p-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Truck size={14} className="text-gray-600" />
                <h2 className="text-sm font-semibold text-gray-900">Nearby Vehicles</h2>
                <span className="text-xs text-gray-500 ml-auto">{filteredVehicles.length} found</span>
              </div>
            </div>
            <div className="max-h-[400px] overflow-y-auto p-2 space-y-2">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
                </div>
              ) : filteredVehicles.length === 0 ? (
                <div className="text-center py-8">
                  <Truck size={24} className="mx-auto text-gray-300" />
                  <p className="text-xs text-gray-500 mt-2">No vehicles found</p>
                </div>
              ) : (
                filteredVehicles.map((v) => (
                  <VehicleCard
                    key={v.vehicleId}
                    vehicle={v}
                    driverName={driverNames[v.driverSno] || v.driverSno || "No driver"}
                    isSelected={selectedVehicleId === v.vehicleId}
                    onSelect={handleSelectVehicle}
                    onBook={handleBook}
                    isBooking={bookingInProgress === v.vehicleId}
                    isValidating={isValidatingVehicle}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Pending Reservation Banner */}
      {pendingReservation && (
        <div className="fixed left-3 right-3 sm:left-auto sm:right-4 z-50 bg-white border border-red-200 shadow-lg rounded-lg p-3 w-auto sm:w-80" style={{bottom: "calc(0.75rem + env(safe-area-inset-bottom))"}}>
          <p className="text-sm font-semibold text-gray-900">Vehicle Reserved</p>
          <p className="text-xs text-gray-600 mt-1">
            Vehicle: {pendingReservation?.vehicle?.vehicleId || "N/A"}
          </p>
          <button
            onClick={handleManualCancelPending}
            disabled={isCancellingPending}
            className="mt-2 w-full px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700 transition disabled:opacity-50"
          >
            {isCancellingPending ? "Cancelling..." : "Cancel Selection"}
          </button>
        </div>
      )}

      {/* Modals */}
      <ProfilePictureCropper
        isOpen={showCropper}
        onClose={() => setShowCropper(false)}
        onCropComplete={async (blob) => {
          setShowCropper(false);
          setIsUploadingPic(true);
          try {
            const formData = new FormData();
            formData.append("ServiceNo", employee.employeeId);
            formData.append("Name", employee.name);
            formData.append("MobileNo", employee.mobileNo);
            formData.append("role", employee.role);
            formData.append("status", employee.status);
            formData.append("password", employee.password || "");
            formData.append("ImageFile", new File([blob], "profile.jpg", { type: "image/jpeg" }));
            
            await api.post("/User/Fileupload", formData, {
              headers: { "Content-Type": "multipart/form-data" },
            });
            
            setEmployee(prev => ({ ...prev, profilePic: `${getProfilePictureUrl(employee.employeeId)}&t=${Date.now()}` }));
            alert("Profile picture updated!");
          } catch (err) {
            console.error(err);
            alert("Failed to update profile picture");
          } finally {
            setIsUploadingPic(false);
          }
        }}
        imageSrc={selectedImage}
      />

      <UserDetailsOverlay
        isVisible={isProfileDetailsVisible}
        onClose={() => setIsProfileDetailsVisible(false)}
        userDetails={employee}
        userRole="Employee"
        fileInputRef={fileInputRef}
        onProfilePictureChange={() => {}}
        isUploadingPic={isUploadingPic}
        onMobileNumberUpdate={async (mobile) => {
          setIsUpdatingMobile(true);
          try {
            const formData = new FormData();
            formData.append("ServiceNo", employee.employeeId);
            formData.append("Name", employee.name);
            formData.append("MobileNo", mobile);
            formData.append("role", employee.role);
            formData.append("status", employee.status);
            formData.append("password", employee.password || "");
            
            await api.post("/User/Fileupload", formData);
            setEmployee(prev => ({ ...prev, mobileNo: mobile }));
            alert("Mobile number updated!");
            return true;
          } catch (err) {
            alert("Failed to update mobile number");
            throw err;
          } finally {
            setIsUpdatingMobile(false);
          }
        }}
        isUpdatingMobile={isUpdatingMobile}
        canEditMobile={true}
        onPasswordChange={async (current, newPass) => {
          setIsUpdatingPassword(true);
          try {
            const formData = new FormData();
            formData.append("ServiceNo", employee.employeeId);
            formData.append("Name", employee.name);
            formData.append("MobileNo", employee.mobileNo);
            formData.append("role", employee.role);
            formData.append("status", employee.status);
            formData.append("password", newPass);
            
            await api.post("/User/Fileupload", formData);
            setEmployee(prev => ({ ...prev, password: newPass }));
            alert("Password changed!");
            return true;
          } catch (err) {
            alert("Failed to change password");
            throw err;
          } finally {
            setIsUpdatingPassword(false);
          }
        }}
        isUpdatingPassword={isUpdatingPassword}
        canChangePassword={true}
      />
    </EmployeeLayout>
  );
}

// Import missing icons
import { ClipboardList } from "lucide-react";